<script>
    import _ from 'lodash'
    import $ from '../shared/helper'
    import { CONST, DEFAULT_CALLBACKS_BUILDER, VGFB_TOUR_ROWS } from '../shared/constants'
    import { FIELDS, buildTreeItems } from '../shared/fieldtypes'
    import VGridformBuilderTour from './v-gridform-builder-tour'
    import VGridformBlock from './v-gridform-block'
    import VGridformBuilderSort from './v-gridform-builder-sort'
    import VGridformBlockSelect from '@src/plugins/vue-gridform/components/v-gridform-block-select'

    const _KEY_INFOS = [
        {key: 'home'},
        {key: 'end'},
        {key: 'cursorUp'},
        {key: 'cursorDown'},
        {divider: true},
        {key: 'ctrlHome'},
        {key: 'ctrlEnd'},
        {key: 'ctrlCursorUp'},
        {key: 'ctrlCursorDown'},
        {divider: true},
        {key: 'cursorRight'},
        {key: 'cursorLeft'},
        {divider: true},
        {key: 'del'}
    ]

    export default {
        name: 'VGridformBuilder',
        components: { VGridformBlockSelect, VGridformBuilderSort, VGridformBlock, VGridformBuilderTour },
        props: {
            value: {
                type: Boolean,
                default: false
            },
            name: {
                type: String,
                default: CONST.GFB_NAME
            },
            configId: {
                type: String,
                default: () => ''
            },
            config: {
                type: Array,
                default: () => []
            },
            types: {
                type: Array,
                default: () => null
            },
            templates: {
                type: Array,
                default: () => null
            },
            blocks: {
                type: Array,
                default: () => null
            },
            blockGroups: {
                type: Array,
                default: () => null
            },
            nodes: {
                type: Array,
                default: () => null
            },
            callbacks: {
                type: Object,
                default: () => null
            },
            autoEdit: {
                type: Boolean,
                default: false
            },
            devTools: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                loaded: false,
                drawer: true,
                gridFormBuilderConfig: {
                    fluid: true,
                    fillHeight: false,
                    padding: 'pa-0',
                    layoutClass: 'mb-6 elevation-4', // card style and more gap
                    fieldlistClass: 'mb-6',
                    fieldstyle: 'outlined',
                    disabled: false,
                    editable: true,
                    stacked: false,
                    validate: false,
                    fieldlistFocusable: true,
                    fieldlistMoveable: true,
                    rating: {
                        large: false,
                        disabled: true
                    },
                    rows: []
                },
                gridFormBuilderCallbacks: {
                    onFieldSelection: this.onFieldSelection.bind(this),
                    onFieldDelete: this.onFieldDelete.bind(this),
                    // forward block creation from the gridform instance
                    onBlockCreate: block => {this.customCallbacks.onBlockCreate(block)},
                    // forward block remove from the gridform instance
                    onBlockRemove: this.onBlockRemove.bind(this),
                    onRowsChange: this.onRowsChange.bind(this),
                    onPrependInnerClick: this.onPrependInnerClick.bind(this),
                },
                treeOpen: [],
                hasSelection: false,
                blockImportDialog: false,
                blockDialog: false,
                blockCurrent: null,
                autoEditMode: false,
                hasRows: false,
                rollbacks: [],
                activeNode: [],
                tourActive: true,
                tourStarted: false,
                tourCleanup: false,
                showSort: false,
                configSort: null,
                keyHandler: null,
                keyInfoDialog: false,
                keyInfos: [],
                importData: '',
                importDataRules: [
                    (data) => !data || $.validateRows(data) || 'Invalid import data'
                ],
                storeKeys: {
                    settings: `${CONST.GFB_NAME}-settings`,
                    treeOpen: `${CONST.GFB_NAME}-tree-open`,
                    tour: `${CONST.GFB_NAME}-tour`,
                }
            }
        },

        computed: {
            gf() {
                return this.$gridform[this.name]
            },
            customCallbacks() {
                return {
                    ...DEFAULT_CALLBACKS_BUILDER,
                    ...this.callbacks
                }
            },
            treeItems() {
                const bti = buildTreeItems(this.types, this.i18n)
                bti.push({
                    id: 'groupuser',
                    label: this.i18n('groups.userdefined'),
                    disabled: _.isEmpty(this.nodes),
                    children: this.nodes
                })
                return bti
            },
            treeItemsSelect() {
                return this.treeItems.filter(ti => ti.id !== 'groupuser')
            },
            // NOTE computed.rows will be used in $watch("rows") in mounted()
            rows() {
                return this.$store.getters[$.ns('rows', this.name)]
            },
            sortedBlocks() {
                const blocks = _.uniqBy(this.blocks, b => {
                    return `${b.label} ${b.hint} ${b.type} ${b.items}`
                }).map(b => ({id: _.uniqueId(b.type), ...b}))
                return _.orderBy(blocks, ['label', 'hint', 'type']).filter(block => block && block.icon)
            },
            blockHint() {
                return `${this.sortedBlocks.length} ${this.i18n('exists')}`
            },
            sortedGroups() {
                const namedBlocks = this.blockGroups.filter(b => !!b.name)
                const blocks = _.uniqBy(namedBlocks, b => `${b.initials} ${b.name} ${b.description}`)
                return _.orderBy(blocks, ['initials', 'name', 'description'])
            },
            groupHint() {
                return `${this.sortedGroups.length} ${this.i18n('exists')}`
            },
            sortedTemplates() {
                return _.orderBy(this.templates, ['initials', 'name', 'description'])
            },
            templatesHint() {
                return `${this.templates.length} ${this.i18n('exists')}`
            },
            dialogWidth() {
                let width
                switch(this.$vuetify.breakpoint.name) {
                    case 'xs': width = '98%'; break
                    case 'sm': width = '80%'; break
                    case 'lg': width = '40%'; break
                    case 'xl': width = '40%'; break
                    default: width = '60%'; break
                }
                return width
            },
            // Autocomplete dropdown menu props
            // max-width, max-height, dense
            menuProps() {
                return {
                    maxWidth: '95vw',
                    maxHeight: 568, // 8px padding top + 10 * 56 (dense list item height)
                    dense: true
                }
            },
            hasRollback() {
                return !_.isEmpty(this.rollbacks)
            },
            expandAllIcon() {
                return CONST.EXPAND_ICON_ALL
            },
            collapseAllIcon() {
                return CONST.COLLAPSE_ICON_ALL
            }
        },
        // NOTE watcher for "rows triggers computed.rows before gridform namespace is registered in store
        watch: {
            value(state) {
                state ? this._bindKeys() : this._unbindKeys()
            },
            config(_rows) {
                const rows = _.cloneDeep(_rows)
                // TODO do not collapse if we only saved a template
                this.makeFieldlistEditable(rows)
                this.gf.rows({rows})
                this.hasRows = !_.isEmpty(rows)
            },
            autoEditMode(autoEditMode) {
                $.updateStore(this.storeKeys.settings, {autoEditMode})
            },
        },

        created() {
            const settings = $.getStore(this.storeKeys.settings, CONST.DEFAULT_GFB_SETTINGS)
            this.treeOpen = $.getStore(this.storeKeys.treeOpen, CONST.DEFAULT_TREE_OPEN)
            this.autoEditMode = $.bool(settings.autoEditMode, this.autoEdit)

            // this.gridFormBuilderConfig.rows = []
            if (!_.isEmpty(this.config)) {
                const rows = _.cloneDeep(this.config)
                this.makeFieldlistEditable(rows)
                this.gridFormBuilderConfig.rows = rows
            }

            // translate keyhandling infos
            this.keyInfos = _KEY_INFOS.map(ki => {
                if (ki.divider) return ki
                return {
                    key: this.i18n(`keyHandling.${ki.key}.key`),
                    title: this.i18n(`keyHandling.${ki.key}.title`)
                }
            })
        },
        mounted() {
            this.hasRows = !_.isEmpty(this.config)
            // NOTE issues with watch.rows, use custom watcher
            this.$watch('rows', _rows => {
                this.hasRows = !_.isEmpty(_rows)
                this.customCallbacks.onRowsChange()
                this.$emit('change', _rows)
            })
            this.$nextTick().then(() => {
                this.loaded = true
                this._bindKeys()
            })
        },
        beforeDestroy() {
            this._unbindKeys()
        },

        methods: {
            _bindKeys() {
                this.keyHandler = e => {
                    // TODO how we can ignore all events from input controls?
                    let validTarget = e.target.nodeName === 'BODY' || e.target.getAttribute('role') === 'document'
                    if (!this.$refs.builder) return
                    if (this.$refs.builder.contains(e.target) && !/(INPUT|TEXTAREA)/i.test(e.target.nodeName)) validTarget = true
                    if (!validTarget) return

                    // move fieldlist up or down
                    if (e.code === 'ArrowUp' && e.ctrlKey) this.gf.fieldlistMove({command: 'up'})
                    else if (e.code === 'ArrowDown' && e.ctrlKey) this.gf.fieldlistMove({command: 'down'})
                    else if (e.code === 'Home' && e.ctrlKey) this.gf.fieldlistMove({command: 'first'})
                    else if (e.code === 'End' && e.ctrlKey) this.gf.fieldlistMove({command: 'last'})
                    // set focus to fieldlist
                    else if (e.code === 'ArrowUp' && !e.ctrlKey) this.gf.fieldlistFocus({command: 'prev'})
                    else if (e.code === 'ArrowDown' && !e.ctrlKey) this.gf.fieldlistFocus({command: 'next'})
                    else if (e.code === 'Home' && !e.ctrlKey) this.gf.fieldlistFocus({command: 'first'})
                    else if (e.code === 'End' && !e.ctrlKey) this.gf.fieldlistFocus({command: 'last'})
                    // set collapsed state for focused fieldlist
                    else if (e.code === 'ArrowLeft') this.gf.fieldlist({toggle: true, collapsed: true})
                    else if (e.code === 'ArrowRight') this.gf.fieldlist({toggle: true, collapsed: false})
                    // remove focused fieldlist
                    else if (e.code === 'Delete') this.onBlockRemove()
                }
                document.addEventListener('keydown', this.keyHandler, true)
            },
            _unbindKeys() {
                if (typeof this.keyHandler !== 'function') return
                document.removeEventListener('keydown', this.keyHandler, true)
                this.keyHandler = null
            },

            async selectType(_item) {
                // NOTE if item.id exists and will be used for fieldlist grouping!!!!!
                const item = _.omit(_item, ['id'])
                const focusedFl = this.gf.fieldlistFocus()

                // ask for position if focused fieldlist exists
                let inGroup = false
                if (_.get(focusedFl, 'fieldlist.id')) {
                    inGroup = await $.cloneBlockConfirmation(item)
                    if (typeof inGroup === 'undefined') return
                }

                item.name = _.uniqueId(`${CONST.GFB_NAME}-${_.now()}-`)

                // append some properties depending on selected type
                if (item.type === 'textarea') {
                    // item.messages = [item.hint]
                    // item.messages.push(`Anzahl Zeilen = ${item.rows}`)
                    // TODO only first message is shown
                    // console.log('item', item)
                }
                else if (item.type === 'radio' || item.type === 'combobox') {
                    item.items = []
                    _.times(3, (i) => {
                        item.items.push({
                            value: `opt${i + 1}`,
                            label: `${this.i18n('rating.option')} ${i + 1}`,
                            avatar: '1'
                        })
                    })
                }
                else if (item.type === 'note') {
                    item.type = 'radio'
                    item.items = []
                    _.times(6, i => {
                        item.items.push({value: `note${i + 1}`, label: `${i + 1}`, avatar: `${6 - i}`})
                    })
                    // item.items.push({
                    //     value: 'note0',
                    //     label: this.$vuetify.lang.t('$vuetify.coaching.noRatingHint'),
                    //     avatar: '0'
                    // })
                    item.multiple = false
                    // NOTE initial empty for score=0
                    item.value = 'note6'
                    item.min = 0
                    item.max = 6
                }

                const rows = [[{...item}]]
                // callback to append rows or modify selected field
                // returns the selected field type
                const { append, edit } = this.customCallbacks.onSelectType(item) || {}
                if (!_.isEmpty(append)) rows.push(append)

                // add to focused fieldlist or to end
                const fieldlist = inGroup ? {...focusedFl.fieldlist} : null
                this.gf.add({ rows, fieldlist })

                if (this.autoEditMode) {
                    this.$nextTick(() => {
                        this.gf.edit(item.name, edit)
                    })
                }
            },
            async cloneBlock(_block) {
                const block = {..._block}
                const focusedFl = this.gf.fieldlistFocus()

                // ask for position if focused fieldlist exists
                let inGroup = false
                if (_.get(focusedFl, 'fieldlist.id')) {
                    const typeLabel = this.i18n(`fields.${block.type}.label`)
                    const typeHint = this.i18n(`fields.${block.type}.hint`)
                    const subtitle = `${typeLabel}: ${typeHint}`
                    inGroup = await $.cloneBlockConfirmation({ ...block, subtitle })
                    if (typeof inGroup === 'undefined') return
                }

                // set unique field name
                block.name = _.uniqueId(`${CONST.GFB_NAME}-${_.now()}-`)
                // remove current fieldlist data
                const rows = [[{..._.omit(block, ['id', 'fieldlist'])}]]
                // add to focused fieldlist or to end
                const fieldlist = inGroup ? {...focusedFl.fieldlist} : null
                this.gf.add({ rows, fieldlist })
            },
            cloneGroup(group) {
                if (!group || !group.rows) return

                const rows = _.cloneDeep(group.rows)
                // make id unique, so we can clone the same group more than once
                const id = _.uniqueId(`${group.id}-${_.now()}-`)
                this.setUniqueFieldName(rows)
                this.setUniqueFieldlistId(rows, id)
                this.makeFieldlistEditable(rows)
                this.gf.add({ rows })
            },
            cloneTemplate(template) {
                const rows = template.config
                this.setUniqueFieldName(rows)
                this.makeFieldlistEditable(rows)
                this.gf.add({ rows })
            },
            buildImportData() {
                try {
                    let rows = $.parseRows(this.importData)
                    if (rows) {
                        rows = rows.filter(row => {
                            const fli = row[0].fieldlist
                            return !fli || (fli.initials.charAt(0) !== '_' && fli.id !== '_NOTES')
                        })
                        this.makeFieldlistEditable(rows)
                        this.gf.add({ rows })
                        this.importData = ''
                    }
                    else {
                        const csv = this.importData.split(',')
                        if (csv.length < 2) return

                        const field = {
                            type: 'slider',
                            label: csv[0],
                            hint: csv[4],
                            tickLabels: csv[2].trim().replace(/ /g, ',')
                        }
                        // console.log('field', field)
                        this.gf.add({ rows: [[field]] })
                        // console.warn('invalid import data')
                    }
                }
                catch (e) {
                    console.warn('import error', e)
                }
            },
            makeFieldlistEditable(rows) {
                rows.forEach(row => {
                    row.forEach(field => {
                        if (field.fieldlist) {
                            field.fieldlist.editable = true
                            // TODO always collapse a new group?
                            // NOTE fieldlist always started collapsed!?
                            // field.fieldlist.collapsed = true
                        }
                        return field
                    })
                })
            },
            setUniqueFieldName(rows) {
                rows.forEach(row => {
                    row.forEach(field => {
                        field.name = _.uniqueId(`${CONST.GFB_NAME}-${_.now()}-`)
                        return field
                    })
                })
            },
            setUniqueFieldlistId(rows, id) {
                rows.forEach(row => {
                    row.forEach(field => {
                        if (field.fieldlist) field.fieldlist.id = id
                        return field
                    })
                })
            },

            filterBlocks(item, queryText) {
                const itemText = `${item.label} ${item.hint}`.toLowerCase()
                return itemText.indexOf(queryText.toLowerCase()) > -1
            },
            filterGroups(item, queryText) {
                const itemText = `${item.label} ${item.hint}`.toLowerCase()
                return itemText.indexOf(queryText.toLowerCase()) > -1
            },
            filterTemplates(item, queryText) {
                const itemText = `${item.description} ${item.name}`.toLowerCase()
                return itemText.indexOf(queryText.toLowerCase()) > -1
            },

            onNodeSelection(node) {
                if (_.isEmpty(node)) return

                const type = node[0]
                const isNode = _.find(this.nodes, n => n.id === type)
                if (isNode) {
                    const rows = [...isNode.rows]
                    const fieldlist = _.omit(isNode, 'rows')
                    fieldlist.foldable = true
                    fieldlist.editable = true
                    this.gf.add({ rows, fieldlist })
                }
                else {
                    const field = { type, ...FIELDS[type] }
                    this.selectType(field)
                }

                this.activeNode = []
            },
            onOpenUpdate(open) {
                $.setStore(this.storeKeys.treeOpen, open)
            },

            sortBlocks() {
                this.showSort = false
                const fls = this.gf.fieldlist()
                this.configSort = _.cloneDeep(fls)
                this.showSort = true
            },
            resort(rows) {
                this.showSort = false
                this.gf.rows({rows})
            },

            onFieldSelection() {
                this.hasSelection = !_.isEmpty(this.gf.select())
            },
            selectGroup() {
                this.hasSelection = !this.hasSelection
                this.gf.select({all: this.hasSelection})
            },
            createGroup() {
                const fls = this.gf.fieldlist()
                const selected = this.gf.select()
                const description = selected.map(name => {
                    const field = this.gf.field({name})
                    return _.size(field) ? field[0].label : ''
                }).join(', ')
                // calc last block group, check for groups in current builder too
                const countGroups = this.blockGroups.length + fls.length + 1
                const fieldlist = {
                    id: _.uniqueId('vgfb-fl-'),
                    initials: `BG-${countGroups}`,
                    name: this.$vuetify.lang.t('$vuetify.gfb.block.title'),
                    description,
                    editable: true,
                    foldable: true
                }
                const update = {}
                _.each(selected, name => {update[name] = {fieldlist}})
                this.gf.update(update) // set fieldlist to selected fields
                this.gf.select({all: false}) // unselect all
                this.gf.fieldlist({focused: fieldlist.id}) // set focus to new fieldlist
                this.hasSelection = false
            },
            saveGroup() {
                this.blockDialog = true
                // TODO auto create description?
                // this.$nextTick(() => {
                //   const selectedFields = this.gf.select()
                //   const fields = this.gf.field().filter(field => selectedFields.includes(field.name))
                //   const description = fields.map(field => field.label).join(', ')
                //   this.$gridform[this.blockName].values({description})
                // })
            },
            createBlock(formdata) {
                this.blockDialog = false
                const block = {...formdata}
                // TODO only selected
                // console.log(this.gf.select())
                block.config = this.gf.export()
                this.customCallbacks.onBlockCreate(block)
            },
            expandGroups() {
                const fieldlistsCollapsed = this.gf.fieldlist().filter(fl => fl.fieldlist.collapsed)
                const fls = fieldlistsCollapsed.map(fl => ({
                    id: fl.fieldlist.id,
                    collapsed: false
                }))
                this.gf.fieldlists(fls)
            },
            collapseGroups() {
                const fieldlistsExpanded = this.gf.fieldlist().filter(fl => !fl.fieldlist.collapsed)
                const fls = fieldlistsExpanded.map(fl => ({
                    id: fl.fieldlist.id,
                    collapsed: true
                }))
                this.gf.fieldlists(fls)
            },
            exportGroup() {
                // show data in dialog
                this.$json.open({
                    select: this.gf.select(),
                    export: this.gf.export(),
                    field: this.gf.field(),
                    fieldlist: this.gf.fieldlist()
                })
            },

            onFieldDelete(row) {
                // don't store the current fieldlist collapsed state
                // when undone, we must use the state at that moment
                row.fieldlist = _.omit(row.fieldlist, ['collapsed'])
                this.rollbacks.push({undo: 'addrow', action: 'delete', row})
            },
            clearRows() {
                this.rollbacks.push({undo: 'rows', action: 'clear', rows: this.gf.rows()})
                this.gf.clear()
            },
            // remove given or focused block/fieldlist
            onBlockRemove(block) {
                const id = block ? block.id : _.get(this.gf.fieldlistFocus(), 'fieldlist.id')
                this.gf.fieldlistRemove({id})
            },
            onRowsChange(rollback) {
                rollback.undo = 'rows'
                this.rollbacks.push(rollback)
            },
            undo() {
                if (_.isEmpty(this.rollbacks)) return

                const lastRollback = _.last(this.rollbacks)
                switch(lastRollback.undo) {
                    case 'addrow':
                        this.gf.add({row: lastRollback.row})
                        break
                    case 'rows':
                        this.gf.rows({rows: lastRollback.rows})
                        break
                }
                this.rollbacks = _.dropRight(this.rollbacks)
            },
            onPrependInnerClick(field) {
                // console.log('field', field)
            },

            startTour() {
                this.tourCleanup = _.isEmpty(this.$store.getters[$.ns('rows', this.name)])
                // TODO set rows depending on lang
                if (this.tourCleanup) this.gf.rows({rows: VGFB_TOUR_ROWS})
                this.tourStarted = true
                this.$tours['vgfbTour'].start()
            },
            stopTour() {
                $.setStore(this.storeKeys.tour, false)
                if (this.tourCleanup) this.gf.clear()
                this.tourStarted = false
            },

            showKeyhandling() {
                this.keyInfoDialog = true
            },

            i18n(key) {
                return this.$vuetify.lang.t(`$vuetify.gfb.${key}`)
            }
        }
    }
</script>

<template>
    <VContainer fluid class="pa-0 fill-height">
        <!-- Toolbar -->
        <VRow class="ma-0">
            <VCol cols="12" class="pt-2 pb-1">
                <VSystemBar id="vgfb-toolbar" class="px-0" color="transparent" height="36">
                    <VTooltip v-if="tourActive" bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="tourStarted" v-on="on" @click.left="startTour">
                                <VIcon color="accent">mdi-comment</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('tourHint') }}</span>
                    </VTooltip>

                    <VDivider class="mx-1 mx-md-2" vertical/>

                    <!-- Keyhandling info dialog -->
                    <VTooltip bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon v-on="on" @click.left="showKeyhandling">
                                <VIcon>mdi-keyboard</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('keyboardInfoHint') }}</span>
                    </VTooltip>

                    <VSpacer/>

                    <VBtnToggle dense>
                        <VTooltip bottom>
                            <template v-slot:activator="{ on }">
                                <VBtn v-model="autoEditMode" icon v-on="on" @click.left="autoEditMode = !autoEditMode">
                                    <VIcon>mdi-square-edit-outline</VIcon>
                                </VBtn>
                            </template>
                            <span>{{ i18n('autoEditHint') }}</span>
                        </VTooltip>
                    </VBtnToggle>

                    <VDivider class="mx-1 mx-md-2" vertical/>

                    <!-- Folding -->
                    <VTooltip bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="!hasRows" v-on="on" @click.left="expandGroups">
                                <VIcon v-text="expandAllIcon"/>
                            </VBtn>
                        </template>
                        <span>{{ i18n('expandHint') }}</span>
                    </VTooltip>
                    <VTooltip bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="!hasRows" v-on="on" @click.left="collapseGroups">
                                <VIcon v-text="collapseAllIcon"/>
                            </VBtn>
                        </template>
                        <span>{{ i18n('collapseHint') }}</span>
                    </VTooltip>

                    <VDivider class="mx-1 mx-md-2" vertical/>

                    <!-- clear / undo -->
                    <VTooltip v-if="hasRollback" bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon v-on="on" @click.left="undo">
                                <VIcon>mdi-undo</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('undoHint') }}</span>
                    </VTooltip>
                    <VTooltip bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="!hasRows" v-on="on" @click.left="clearRows">
                                <VIcon>mdi-delete-circle-outline</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('clearHint') }}</span>
                    </VTooltip>

                    <VDivider class="mx-1 mx-md-2" vertical/>

                    <!-- sort blocks -->
                    <VTooltip v-if="devTools" bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="!hasRows" v-on="on" @click.left="sortBlocks">
                                <VIcon color="accent">mdi-drag-variant</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('sortHint') }}</span>
                    </VTooltip>

                    <!-- select blocks -->
                    <VTooltip bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="!hasRows" v-on="on" @click.left="selectGroup">
                                <VIcon>mdi-select-group</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('selectHint') }}</span>
                    </VTooltip>

                    <!-- create block group -->
                    <VTooltip bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="!hasSelection" v-on="on" @click.left="createGroup">
                                <VIcon>mdi-group</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('createGroupHint') }}</span>
                    </VTooltip>

                    <VDivider class="mx-1 mx-md-2" vertical/>

                    <!-- export blocks / groups -->
                    <VTooltip v-if="devTools" bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="!hasRows" v-on="on" @click.left="exportGroup">
                                <VIcon color="accent">mdi-file-export-outline</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('exportHint') }}</span>
                    </VTooltip>

                    <!-- save block group -->
                    <VTooltip bottom>
                        <template v-slot:activator="{ on }">
                            <VBtn icon :disabled="!hasSelection" v-on="on" @click.left="saveGroup">
                                <VIcon>mdi-content-save-outline</VIcon>
                            </VBtn>
                        </template>
                        <span>{{ i18n('saveHint') }}</span>
                    </VTooltip>
                </VSystemBar>
            </VCol>
        </VRow>

        <!-- Builder -->
        <VRow class="mx-0 fill-height">
            <!-- Imports and Selections -->
            <VCol cols="6" md="3" sm="3" class="vgfb__container vgfb__sidebar">
                <VContainer fluid class="pa-0">
                    <slot name="sidebar"></slot>

                    <!-- Import from existing templates -->
                    <VSheet id="vgfb-tpl-import" class="px-5">
                        <VAutocomplete
                            :label="i18n('tplImport')"
                            :items="sortedTemplates"
                            :filter="filterTemplates"
                            return-object
                            item-value="templateId"
                            item-text="name"
                            :menu-props="menuProps"
                            spellcheck="false"
                            dense
                            filled
                            :hint="templatesHint"
                            persistent-hint
                            :disabled="!loaded"
                            @input="cloneTemplate">
                            <template v-slot:item="{ item }">
                                <VListItemContent>
                                    <!-- TODO this prop names must be configurable -->
                                    <VListItemTitle>
                                        <span class="font-weight-bold pr-4">{{ item.initials }}</span>
                                        <span class="font-weight-regular">{{ item.name }}</span>
                                    </VListItemTitle>
                                    <VListItemSubtitle v-text="item.description"/>
                                </VListItemContent>
                                <VListItemAction>
                                </VListItemAction>
                            </template>
                        </VAutocomplete>
                    </VSheet>

                    <!-- Import from existing groups -->
                    <VSheet id="vgfb-group-import" class="px-5">
                        <VAutocomplete
                            :label="i18n('groupImport')"
                            :items="sortedGroups"
                            :filter="filterGroups"
                            return-object
                            item-value="id"
                            item-text="initials"
                            :menu-props="menuProps"
                            spellcheck="false"
                            dense
                            filled
                            :hint="groupHint"
                            persistent-hint
                            :disabled="!loaded"
                            @input="cloneGroup">
                            <template v-slot:item="{ item }">
                                <VListItemAvatar>
                                    <VIcon color="accent">{{ item.rows.length }}</VIcon>
                                </VListItemAvatar>
                                <VListItemContent>
                                    <VListItemTitle v-text="item.name"/>
                                    <VListItemSubtitle v-text="item.description"/>
                                </VListItemContent>
                            </template>
                        </VAutocomplete>
                    </VSheet>

                    <!-- Import from existing fields -->
                    <VSheet id="vgfb-block-import" class="px-5 pb-4">
                        <VAutocomplete
                            :label="i18n('blockImport')"
                            :items="sortedBlocks"
                            :filter="filterBlocks"
                            return-object
                            item-value="id"
                            item-text="label"
                            :menu-props="menuProps"
                            spellcheck="false"
                            dense
                            filled
                            :hint="blockHint"
                            persistent-hint
                            :disabled="!loaded"
                            @input="cloneBlock">
                            <template v-slot:item="{ item }">
                                <VListItemAvatar>
                                    <VIcon>{{ item.icon }}</VIcon>
                                </VListItemAvatar>
                                <VListItemContent>
                                    <VListItemTitle v-text="item.label"/>
                                    <VListItemSubtitle v-text="item.hint"/>
                                </VListItemContent>
                            </template>
                        </VAutocomplete>
                        <VBtn v-if="devTools" class="mt-4" color="accent" text block @click.left="blockImportDialog = true">
                            {{i18n('blockImport')}}
                        </VBtn>
                    </VSheet>

                    <!-- Field type selection tree -->
                    <VSheet id="vgfb-type-select">
                        <VTreeview
                            :active.sync="activeNode"
                            :items="treeItems"
                            item-text="label"
                            :open="treeOpen"
                            activatable
                            open-on-click
                            dense
                            @update:open="onOpenUpdate"
                            @update:active="onNodeSelection">
                            <template v-slot:prepend="{ item }">
                                <VIcon v-if="item && item.icon" small>{{ item.icon }}</VIcon>
                            </template>
                        </VTreeview>
                    </VSheet>

                    <!-- DEV import from text input -->
                    <VTextarea
                        v-if="devTools"
                        v-model="importData"
                        placeholder="Daten zum Import hier einfügen"
                        class="px-2 my-4"
                        color="accent"
                        outlined
                        hide-details
                        :rows="5"
                        :rules="importDataRules"
                        :spellcheck="false"
                    />
                    <VBtn v-if="importData" text @click.left="buildImportData">Importieren</VBtn>
                </VContainer>
            </VCol>

            <!-- Builder preview -->
            <VCol ref="builder" cols="6" md="9" sm="9" class="vgfb__container vgfb__preview">
                <VGridform
                    :name="name"
                    :config="gridFormBuilderConfig"
                    :callbacks="gridFormBuilderCallbacks"
                    :dev-tools="devTools"/>
            </VCol>
        </VRow>

        <VDialog v-if="devTools" v-model="blockImportDialog">
            <VGridformBlockSelect :blocks="sortedBlocks" :items="treeItemsSelect"/>
        </VDialog>

        <VGridformBuilderSort v-if="devTools"
            v-model="showSort"
            :config="configSort"
            @close="showSort = false"
            @submit="resort"/>

        <!-- Edit block metadata -->
        <VDialog v-model="blockDialog" :max-width="dialogWidth" persistent>
            <VGridformBlock :block="blockCurrent" :dev-tools="devTools" @cancel="blockDialog = false"/>
        </VDialog>

        <!-- Introduction tour -->
        <VGridformBuilderTour @finished="stopTour"/>

        <!-- Show keyboard handling -->
        <VDialog v-model="keyInfoDialog" :max-width="dialogWidth">
            <VCard>
                <VCardTitle class="justify-center">{{ i18n('keyboardInfoHint') }}</VCardTitle>
                <VCardText>
                    <VList dense>
                        <template v-for="(item, index) in keyInfos">
                            <VDivider v-if="item.divider" :key="index"/>
                            <VListItem v-else :key="item.key">
                                <VListItemContent>
                                    <VListItemTitle>{{ item.title }}</VListItemTitle>
                                </VListItemContent>
                                <VListItemAction>
                                    <VListItemActionText>{{ item.key }}</VListItemActionText>
                                </VListItemAction>
                            </VListItem>
                        </template>
                    </VList>
                </VCardText>
                <VCardActions>
                    <VSpacer/>
                    <VBtn block text color="primary" @click="keyInfoDialog = false">{{ $vuetify.lang.t('$vuetify.actions.ok') }}</VBtn>
                </VCardActions>
            </VCard>
        </VDialog>
    </VContainer>
</template>

<style scoped>
    .vgfb__container {
        height: calc(100% - 56px);
        padding-right: 0;
        padding-left: 0;
        overflow: auto;
    }
    .vgfb__sidebar {
        padding: 6px 12px;
    }
</style>
<style>
    .app-bp-lg .vgfb__preview {
        padding-right: 32px;
        padding-left: 32px;
    }
    .app-bp-xl .vgfb__preview {
        padding-right: 64px;
        padding-left: 64px;
    }

    /* hide empty node icon placeholder, has min-width 24px */
    .v-treeview-node__prepend:empty {
        display: none;
    }
</style>
